import {
  Button,
  DatePicker,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeading,
  Divider,
  SearchInput,
  SelectField,
} from 'omakase';
import { CalendarWithQuickFilter } from 'components/search/calendar/CalendarWithQuickFilter';
import { FilterType, OfferingSearchTypeOrBusinessType } from 'utils/searchBar';
import { LocalDate } from 'js-joda';
import {
  NO_SEARCH_UPDATES_SENTINEL,
  OnSearchType,
} from 'components/www/availability-search/QuerySearchBarButton';
import { SearchResultsOpenMode, useIsDateQuickFilterEnabled } from 'components/search/utils';
import { TimeOption } from 'utils/timeUtils';
import { consumer } from '@tock/coretypes';
import {
  dateSection,
  dialogContent,
  select,
} from 'components/www/availability-search/filters/adjustSearchDialogCssDefs';

interface AdjustSearchDialogProps {
  isAdjustSearchDialogOpen: () => boolean;
  handleCloseSearch: () => void;
  searchLabel: string | undefined;
  visibleFilters: FilterType[];
  openMode: string;
  size: string | undefined;
  onChangeSize: (size: string) => void;
  sizeOptions: Option[];
  dateEnd: string | undefined;
  time: string | undefined;
  onChangeTime: (time: string) => void;
  displayedTimes: TimeOption[];
  type: string | undefined;
  onChangeType: (type: consumer.OfferingSearchType) => void;
  typeOptions: Option[];
  startDate: LocalDate;
  endDate: LocalDate;
  date: string | undefined;
  onChangeDate: (date: string) => void;
  q: string | string[] | undefined;
  onChangeQ: (q: string) => void;
  setOpenMode: React.Dispatch<React.SetStateAction<SearchResultsOpenMode>>;
  onSearch: OnSearchType;
  onChangeDateRange?(startDate?: LocalDate, endDate?: LocalDate): void;
}

const AdjustSearchDialog: React.FunctionComponent<AdjustSearchDialogProps> = ({
  isAdjustSearchDialogOpen,
  handleCloseSearch,
  searchLabel,
  visibleFilters,
  openMode,
  size,
  onChangeSize,
  sizeOptions,
  dateEnd,
  time,
  onChangeTime,
  displayedTimes,
  onChangeType,
  type,
  typeOptions,
  startDate,
  endDate,
  date,
  onChangeDate,
  onChangeQ,
  setOpenMode,
  onSearch,
  q,
  onChangeDateRange,
}) => {
  const flattendedQueryValue = typeof q === 'string' ? q : q?.toString();

  const useQuickFilter = useIsDateQuickFilterEnabled(type as OfferingSearchTypeOrBusinessType);

  const selectedDateAsLocalDate = dateEnd || !date ? undefined : LocalDate.parse(date);

  const handleChangeDate = (date: LocalDate | undefined) => {
    const dateAsString = date?.toString();
    if (dateAsString) {
      onChangeDate(dateAsString);
    }
  };

  return (
    <Dialog
      variant="fullScreen"
      aria-labelledby="search-dialog"
      open={isAdjustSearchDialogOpen()}
      onClose={handleCloseSearch}
    >
      <DialogHeading id="search-dialog" onClose={handleCloseSearch}>
        {searchLabel}
      </DialogHeading>
      <DialogContent css={dialogContent} disablePadding>
        {visibleFilters.includes('DATE') &&
          openMode === 'SEARCH_OPEN' &&
          (useQuickFilter ? (
            <CalendarWithQuickFilter
              startDate={startDate}
              endDate={endDate}
              date={date}
              dateEnd={dateEnd}
              onChangeDate={onChangeDate}
              onChangeDateRange={onChangeDateRange}
            />
          ) : (
            <>
              <div css={dateSection}>
                <DatePicker
                  latestSelectableDate={endDate}
                  selectionType="single"
                  selectedDate={selectedDateAsLocalDate}
                  onDateSelected={handleChangeDate}
                />
              </div>
              {visibleFilters.includes('TIME') && <Divider variant="thin" position="middle" />}
            </>
          ))}
        {visibleFilters.includes('SIZE') && openMode === 'SEARCH_OPEN' && (
          <div css={select}>
            <SelectField
              fullWidth
              name="size"
              id="size"
              value={size}
              variant="inverse"
              label="Party size"
              onChange={(
                event: React.ChangeEvent<{
                  name?: string | undefined;
                  value: unknown;
                }>
              ) => onChangeSize(event.target.value as string)}
              inputProps={{ 'aria-label': 'Size' }}
            >
              <option value="">Party size</option>
              {sizeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
          </div>
        )}
        {visibleFilters.includes('TIME') && openMode === 'SEARCH_OPEN' && !dateEnd && (
          <div css={select}>
            <SelectField
              fullWidth
              name="time"
              id="time"
              value={time}
              variant="inverse"
              label="Time"
              onChange={(
                event: React.ChangeEvent<{
                  name?: string | undefined;
                  value: unknown;
                }>
              ) => onChangeTime(event.target.value as string)}
              inputProps={{ 'aria-label': 'Time' }}
            >
              <option value="" />
              {displayedTimes.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
          </div>
        )}
        {visibleFilters.includes('TYPE') && openMode === 'TYPE_OPEN' && (
          <div>
            <SelectField
              fullWidth
              name="type"
              id="type"
              value={type}
              onChange={(
                event: React.ChangeEvent<{
                  name?: string | undefined;
                  value: unknown;
                }>
              ) => onChangeType(event.target.value as consumer.OfferingSearchType)}
              inputProps={{ 'aria-label': 'Type' }}
            >
              <option value="" />
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
          </div>
        )}
        {visibleFilters.includes('TEXT') && openMode === 'TEXT_OPEN' && (
          <div css={select}>
            <SearchInput
              placeholder="Search"
              size="large"
              fullWidth
              name="q"
              id="q"
              value={flattendedQueryValue}
              onChange={(updatedValue: string) => onChangeQ(updatedValue)}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions stack="mobile">
        {visibleFilters.includes('TEXT') && openMode === 'TEXT_OPEN' && (
          <Button
            size="large"
            variant="basic"
            fullWidth
            onClick={() => {
              onChangeQ('');
              setOpenMode('CLOSED');
              onSearch({ q: '' });
            }}
          >
            Clear
          </Button>
        )}
        <Button
          size="large"
          variant="primary"
          fullWidth
          onClick={() => {
            setOpenMode('CLOSED');
            onSearch(NO_SEARCH_UPDATES_SENTINEL);
          }}
        >
          Update search
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdjustSearchDialog;
